'use client';

import Icon from '@/components/ui/AppIcon';
import { MilestoneRecommendation, ServiceCategory } from '../utils/milestoneRules';

interface ServiceChecklistProps {
  make: string;
  model: string;
  year: number;
  mileage: number;
  recommendation: MilestoneRecommendation;
}

export default function ServiceChecklist({ make, model, year, mileage, recommendation }: ServiceChecklistProps) {
  if (!recommendation) return null;

  const handlePrint = () => {
    window.print();
  };

  const renderCategory = (category: ServiceCategory, iconName: string, iconColorClass: string) => {
    if (!category.items || category.items.length === 0) return null;

    return (
      <div className="mb-8">
        <h3 className="flex items-center text-xl font-heading font-semibold text-text-primary mb-4">
          <Icon name={iconName as any} size={24} className={`mr-2 ${iconColorClass}`} />
          {category.title}
        </h3>
        <div className="bg-card rounded-2xl shadow-elevation-sm border border-border overflow-hidden">
          <div className="divide-y divide-border">
            {category.items.map((service, idx) => (
              <div key={idx} className="p-4 sm:p-5 hover:bg-muted/50 transition-smooth flex items-start space-x-4">
                <div className="mt-1 flex-shrink-0">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${service.isImportant ? 'border-primary' : 'border-muted-foreground'}`}
                  >
                    <div className={`w-2.5 h-2.5 rounded-full ${service.isImportant ? 'bg-primary' : 'bg-transparent'}`} />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className={`text-base font-medium ${service.isImportant ? 'text-text-primary' : 'text-text-primary'}`}>
                    {service.name}
                  </h4>
                  {service.description && (
                    <p className="mt-1 text-sm text-text-secondary">
                      {service.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-text-primary">
            {year} {make} {model} - {mileage.toLocaleString()} Mile Service
          </h2>
          <p className="text-text-secondary mt-1 font-body">
            A comprehensive breakdown of what actually matters at this milestone.
          </p>
        </div>
        <button
          onClick={handlePrint}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-smooth print:hidden"
        >
          <Icon name="PrinterIcon" size={20} />
          <span>Print Checklist</span>
        </button>
      </div>

      {recommendation.bluntTake.length > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-6">
          <h3 className="flex items-center text-primary font-bold mb-3">
            <Icon name="LightBulbIcon" size={20} className="mr-2" />
            ⚙️ Blunt Take
          </h3>
          <ul className="space-y-2">
            {recommendation.bluntTake.map((note, idx) => (
              <li key={idx} className="text-text-primary text-sm font-medium">
                {note}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-2">
          {renderCategory(recommendation.core, 'CheckCircleIcon', 'text-green-500')}
          {renderCategory(recommendation.fluids, 'ArrowPathRoundedSquareIcon', 'text-blue-500')}
          {renderCategory(recommendation.wearAndTear, 'MagnifyingGlassIcon', 'text-amber-500')}
          {renderCategory(recommendation.lessCommon, 'ExclamationTriangleIcon', 'text-red-400')}
        </div>

        <div className="space-y-6">
          <div className="bg-card rounded-2xl shadow-elevation-sm border border-border p-6">
            <h3 className="flex items-center text-lg font-heading font-semibold text-text-primary mb-4">
              <Icon name="CurrencyDollarIcon" size={24} className="mr-2 text-green-600" />
              💰 Typical Cost Range
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-text-secondary">Basic Service:</p>
                <p className="font-bold text-lg text-text-primary">{recommendation.costRange.basic}</p>
              </div>
              <div>
                <p className="text-sm text-text-secondary">With fluid replacements + extras:</p>
                <p className="font-bold text-lg text-text-primary">{recommendation.costRange.withExtras}</p>
              </div>
              {recommendation.costRange.note && (
                <p className="text-xs text-text-secondary italic mt-2">
                  {recommendation.costRange.note}
                </p>
              )}
            </div>
          </div>

          <div className="bg-card rounded-2xl shadow-elevation-sm border border-border p-6">
            <h3 className="flex items-center text-lg font-heading font-semibold text-text-primary mb-4">
              <Icon name="ChatBubbleBottomCenterTextIcon" size={24} className="mr-2 text-purple-500" />
              🧠 Practical Advice
            </h3>
            <ul className="space-y-3">
              {recommendation.practicalAdvice.map((advice, idx) => (
                <li key={idx} className="text-sm text-text-secondary flex items-start">
                  <span className="mr-2 mt-0.5">•</span>
                  <span>{advice}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
